import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  AMAZON_MIN_DAILY_VOLUME,
  UNIUNI_MIN_DAILY_VOLUME,
} from "@redotech/redo-fulfillment-carrier-common/carrier-eligibility-requirements";
import { UpsServices } from "@redotech/redo-fulfillment-carrier-common/carrier-service-enums";
import {
  PricingStrategy,
  SpreadUnit,
} from "@redotech/redo-fulfillment-outbound-labels-common/carrier-quote";
import { RateTableCarrier } from "@redotech/redo-fulfillment-outbound-labels-common/rate-table-carrier";
import { RateProvider } from "@redotech/redo-fulfillment-rate-common/outbound-labels";
import {
  estimateWeekdays,
  getDhlPickupCostPerPickup,
  MissingMerchantRateMode,
  REV_SHARE_CARRIERS,
  UpsRateType,
  WEEKDAYS_PER_WEEK,
} from "@redotech/redo-fulfillment-rate-table-common/pricing-calculator";
import {
  AxisKind,
  OutboundWeightByZoneRateTable,
  RateTableType,
  RateTableValueMode,
} from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import { rateTableCarrierLabel } from "@redotech/redo-fulfillment-rate-table-common/rate-table-mapping";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { RedoAdminRpcClientContext } from "../../../../../app/redo-admin-rpc-client-provider";
import { TeamContext } from "../../../../team";
import {
  ComparisonRateTable,
  ViewMode,
} from "../components/comparison-rate-table";
import { MixedModeRateTable } from "../components/mixed-mode-rate-table";
import { useRateCalculator } from "../hooks";
import { type ColorMode, type RateCalcCarrier } from "../types";
import { countWeekdays } from "../utils/date-utils";
import {
  calculateMixedModeResults,
  calculateQuoteResults,
  getBaselineTableForCarrier,
} from "../utils/pricing-calculator";
import * as styles from "./pricing-config-step.module.css";

interface PricingConfigStepProps {
  canCreateRateTable?: boolean;
  teamId?: string | null;
}

export const PricingConfigStep = memo(function PricingConfigStep({
  canCreateRateTable = true,
  teamId: teamIdProp,
}: PricingConfigStepProps) {
  const { state, dispatch } = useRateCalculator();
  const teamFromContext = useContext(TeamContext);
  const client = useRequiredContext(RedoAdminRpcClientContext);

  const teamId = teamIdProp ?? teamFromContext?._id;
  const isUspsMarketRatesMode = state.dataSource === "usps_market_rates";

  const [isMixedMode, setIsMixedMode] = useState(!isUspsMarketRatesMode);
  const [selectedCarrier, setSelectedCarrier] =
    useState<RateCalcCarrier | null>(
      isUspsMarketRatesMode ? (state.selectedCarriers[0] ?? null) : null,
    );
  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.Savings);
  const exportRef = useRef<(() => void) | null>(null);
  const [savePending, setSavePending] = useState(false);
  const [dailyVolume, setDailyVolume] = useState(state.dailyVolume || 100);
  const [colorMode, setColorMode] = useState<ColorMode>("carrier");

  useEffect(() => {
    if (isUspsMarketRatesMode && isMixedMode) {
      setIsMixedMode(false);
      setSelectedCarrier(state.selectedCarriers[0] ?? null);
    }
  }, [isUspsMarketRatesMode, isMixedMode, state.selectedCarriers]);

  useEffect(() => {
    if (state.totalShipments > 0 && state.dailyVolume === 0) {
      const weekdays =
        state.oldestShipmentDate && state.newestShipmentDate
          ? countWeekdays(
              new Date(state.oldestShipmentDate),
              new Date(state.newestShipmentDate),
            )
          : estimateWeekdays(90);

      const calculatedDailyVolume = Math.round(state.totalShipments / weekdays);
      if (calculatedDailyVolume > 0) {
        setDailyVolume(calculatedDailyVolume);
        dispatch({ type: "SET_DAILY_VOLUME", volume: calculatedDailyVolume });
      }
    }
  }, [
    state.totalShipments,
    state.oldestShipmentDate,
    state.newestShipmentDate,
    state.dailyVolume,
    dispatch,
  ]);

  useEffect(() => {
    if (state.dailyVolume > 0 && state.dailyVolume !== dailyVolume) {
      setDailyVolume(state.dailyVolume);
    }
  }, [state.dailyVolume, dailyVolume]);

  const handleDailyVolumeChange = useHandler((volume: number) => {
    setDailyVolume(volume);
    dispatch({ type: "SET_DAILY_VOLUME", volume });
  });

  const carrierStats = useMemo(() => {
    if (
      isUspsMarketRatesMode ||
      !state.rateGrid ||
      !state.baselineRateTables ||
      !state.carrierEligibility
    ) {
      return new Map<RateCalcCarrier, number>();
    }

    const stats = new Map<RateCalcCarrier, number>();
    for (const carrier of state.selectedCarriers) {
      const results = calculateQuoteResults(
        state.baselineRateTables,
        state.rateGrid,
        [carrier],
        state.carrierEligibility,
        state.pricingStrategy,
        state.unit,
        state.amount,
        state.dhlSettings,
        state.dailyVolume,
        state.revShareSettings,
      );
      stats.set(carrier, results.summary.ordersWonPct);
    }
    return stats;
  }, [
    isUspsMarketRatesMode,
    state.rateGrid,
    state.baselineRateTables,
    state.carrierEligibility,
    state.selectedCarriers,
    state.pricingStrategy,
    state.unit,
    state.amount,
    state.dhlSettings,
    state.dailyVolume,
    state.revShareSettings,
  ]);

  const mixedResults = useMemo(() => {
    if (
      isUspsMarketRatesMode ||
      !state.rateGrid ||
      !state.baselineRateTables ||
      !state.carrierEligibility
    ) {
      return null;
    }
    return calculateMixedModeResults(
      state.baselineRateTables,
      state.rateGrid,
      state.selectedCarriers,
      state.carrierEligibility,
      state.pricingStrategy,
      state.unit,
      state.amount,
      state.revShareSettings,
      state.dailyVolume,
    );
  }, [
    isUspsMarketRatesMode,
    state.rateGrid,
    state.baselineRateTables,
    state.carrierEligibility,
    state.selectedCarriers,
    state.pricingStrategy,
    state.unit,
    state.amount,
    state.revShareSettings,
    state.dailyVolume,
  ]);

  const handleDownloadRateCard = useCallback(() => {
    if (exportRef.current) {
      exportRef.current();
    }
  }, []);

  const handleCreateRateTable = useHandler(async () => {
    if (!selectedCarrier) {
      toast("No carrier selected", { variant: "error" });
      return;
    }

    const baselineTable = getBaselineTableForCarrier(
      state.baselineRateTables,
      selectedCarrier,
      state.carrierEligibility,
    );

    if (!baselineTable) {
      toast("No baseline table found for this carrier", { variant: "error" });
      return;
    }

    let spreadValue = state.amount;

    if (
      selectedCarrier === RateTableCarrier.DHL_ECS &&
      state.carrierEligibility?.dhl
    ) {
      const pickupCostPerPickup = getDhlPickupCostPerPickup(
        state.carrierEligibility.dhl.nearestDC,
        state.carrierEligibility.dhl.distanceMiles,
        state.dhlSettings.needsGL,
      );
      const weeklyPickupCost =
        pickupCostPerPickup * state.dhlSettings.weeklyPickups;
      const weeklyVolume = state.dailyVolume * WEEKDAYS_PER_WEEK;
      const pickupCostPerPackage =
        weeklyVolume > 0 ? weeklyPickupCost / weeklyVolume : 0;
      spreadValue = state.amount + pickupCostPerPackage;
    }

    const spreadData = baselineTable.data.map((row) =>
      row.map(() => spreadValue.toFixed(2)),
    );

    let provider: RateProvider;
    let serviceLevel: OutboundWeightByZoneRateTable["serviceLevel"];
    let distributionCenterId: string | undefined;

    switch (selectedCarrier) {
      case RateTableCarrier.DHL_ECS:
        provider = RateProvider.DHL_ECOMMERCE;
        serviceLevel = "Default";
        distributionCenterId = state.carrierEligibility?.dhl?.nearestDC;
        break;
      case RateTableCarrier.UPS:
        provider = RateProvider.UPS;
        serviceLevel = UpsServices.Ground;
        break;
      case RateTableCarrier.Amazon:
        provider = RateProvider.SOAP_BOX;
        serviceLevel = "Default";
        break;
      case RateTableCarrier.USPS:
        provider = RateProvider.EASYPOST;
        serviceLevel = "Default";
        break;
      case RateTableCarrier.UNIUNI:
        provider = RateProvider.EASYPOST;
        serviceLevel = "Default";
        break;
      default:
        toast("Unsupported carrier", { variant: "error" });
        return;
    }

    const rateTable: OutboundWeightByZoneRateTable = {
      rateTableType: RateTableType.OUTBOUND_WEIGHT_BY_ZONE,
      valueMode: RateTableValueMode.Spread,
      provider,
      carrier: selectedCarrier,
      serviceLevel,
      distributionCenterId:
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        distributionCenterId as typeof rateTable.distributionCenterId,
      xAxis: { kind: AxisKind.ZONE, values: baselineTable.xAxis.values },
      yAxis: {
        kind: AxisKind.WEIGHT,
        units: baselineTable.yAxis.units,
        values: baselineTable.yAxis.values,
      },
      data: spreadData,
      defaultValue: spreadValue.toFixed(2),
    };

    setSavePending(true);
    try {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      await client.saveTeamRateTable({ teamId: teamId!, rateTable });
      toast(
        `Rate table created for ${rateTableCarrierLabel(selectedCarrier)}`,
        { variant: "success" },
      );
      dispatch({ type: "CLOSE_WIZARD" });
      dispatch({ type: "RESET" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast(`Failed to create rate table: ${message}`, { variant: "error" });
    } finally {
      setSavePending(false);
    }
  });

  const canContinue =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const isRevShareCarrier =
    selectedCarrier &&
    (REV_SHARE_CARRIERS as readonly RateCalcCarrier[]).includes(
      selectedCarrier,
    );

  return (
    <Flex dir="column" gap="lg">
      <Flex gap="sm">
        <CarrierTab
          active={isMixedMode}
          carrier={null}
          hideStats={false}
          key="mixed"
          onClick={() => {
            setIsMixedMode(true);
            setSelectedCarrier(null);
          }}
          winnableOrdersPct={mixedResults?.summary.ordersWonPct ?? 0}
        />
        {state.selectedCarriers.map((carrier) => (
          <CarrierTab
            active={!isMixedMode && selectedCarrier === carrier}
            carrier={carrier}
            hideStats={isUspsMarketRatesMode}
            key={carrier}
            onClick={() => {
              setIsMixedMode(false);
              setSelectedCarrier(carrier);
            }}
            winnableOrdersPct={carrierStats.get(carrier) ?? 0}
          />
        ))}
      </Flex>

      {selectedCarrier === RateTableCarrier.Amazon &&
        dailyVolume < AMAZON_MIN_DAILY_VOLUME && (
          <Flex
            align="center"
            borderRadius="lg"
            gap="sm"
            p="md"
            style={{
              background: "var(--redo-colors-background-bg-warning-primary)",
              border:
                "1px solid var(--redo-component-colors-utility-warning-utility-warning-300)",
            }}
          >
            <Text fontSize="sm" fontWeight="semibold" textColor="warning">
              ⚠ Volume requirement not met
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Amazon requires {AMAZON_MIN_DAILY_VOLUME}+ orders/day. Current
              volume: {Math.round(dailyVolume)}/day. Rates shown are estimates
              only.
            </Text>
          </Flex>
        )}
      {selectedCarrier === RateTableCarrier.UNIUNI &&
        dailyVolume < UNIUNI_MIN_DAILY_VOLUME && (
          <Flex
            align="center"
            borderRadius="lg"
            gap="sm"
            p="md"
            style={{
              background: "var(--redo-colors-background-bg-warning-primary)",
              border:
                "1px solid var(--redo-component-colors-utility-warning-utility-warning-300)",
            }}
          >
            <Text fontSize="sm" fontWeight="semibold" textColor="warning">
              ⚠ Volume requirement not met
            </Text>
            <Text fontSize="sm" textColor="secondary">
              UniUni requires {UNIUNI_MIN_DAILY_VOLUME}+ orders/day. Current
              volume: {Math.round(dailyVolume)}/day. Rates shown are estimates
              only.
            </Text>
          </Flex>
        )}
      {isMixedMode ? (
        <MixedDetailView
          colorMode={colorMode}
          dailyVolume={dailyVolume}
          onColorModeChange={setColorMode}
          onDailyVolumeChange={handleDailyVolumeChange}
        />
      ) : selectedCarrier ? (
        <CarrierDetailView
          carrier={selectedCarrier}
          dailyVolume={dailyVolume}
          exportRef={exportRef}
          onDailyVolumeChange={handleDailyVolumeChange}
          onViewModeChange={setViewMode}
          viewMode={viewMode}
        />
      ) : null}

      <Flex gap="md" justify="space-between" pt="md">
        <RedoButton
          hierarchy="secondary"
          onClick={() => dispatch({ type: "SET_STEP", step: 2 })}
          size="md"
          text="Back"
        />
        <Flex gap="sm">
          <RedoButton
            disabled={!exportRef.current}
            hierarchy="secondary"
            onClick={handleDownloadRateCard}
            size="md"
            text="Export CSV"
          />
          {canCreateRateTable && (
            <RedoButton
              disabled={
                !canContinue ||
                !selectedCarrier ||
                isMixedMode ||
                savePending ||
                !!isRevShareCarrier
              }
              hierarchy="primary"
              onClick={handleCreateRateTable}
              pending={savePending}
              size="md"
              text="Create Rate Table"
            />
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const CarrierTab = memo(function CarrierTab({
  carrier,
  active,
  winnableOrdersPct,
  hideStats,
  onClick,
}: {
  carrier: RateCalcCarrier | null;
  active: boolean;
  winnableOrdersPct: number;
  hideStats?: boolean;
  onClick: () => void;
}) {
  const label = carrier ? rateTableCarrierLabel(carrier) : "Mixed";

  return (
    <Flex
      align="center"
      borderRadius="lg"
      className={active ? styles.carrierTabActive : styles.carrierTabInactive}
      dir="column"
      gap="xs"
      onClick={onClick}
      px="lg"
      py="md"
    >
      <Text
        as="span"
        className={
          active ? styles.carrierTabLabelActive : styles.carrierTabLabelInactive
        }
      >
        {label}
      </Text>
      {!hideStats && (
        <>
          <Text
            as="span"
            className={
              active
                ? styles.carrierTabPercentageActive
                : styles.carrierTabPercentageInactive
            }
          >
            {winnableOrdersPct.toFixed(0)}%
          </Text>
          <Text
            as="span"
            className={
              active
                ? styles.carrierTabWinnableActive
                : styles.carrierTabWinnableInactive
            }
          >
            winnable
          </Text>
        </>
      )}
    </Flex>
  );
});

const CarrierDetailView = memo(function CarrierDetailView({
  carrier,
  viewMode,
  onViewModeChange,
  dailyVolume,
  onDailyVolumeChange,
  exportRef,
}: {
  carrier: RateCalcCarrier;
  viewMode: ViewMode;
  onViewModeChange: (mode: ViewMode) => void;
  dailyVolume: number;
  onDailyVolumeChange: (volume: number) => void;
  exportRef?: React.MutableRefObject<(() => void) | null>;
}) {
  const { state, dispatch } = useRateCalculator();

  const isRevShareCarrier = (
    REV_SHARE_CARRIERS as readonly RateCalcCarrier[]
  ).includes(carrier);
  const isDhlSelected =
    carrier === RateTableCarrier.DHL_ECS &&
    state.carrierEligibility?.dhl?.eligible;

  const handleStrategyChange = useHandler((strategy: PricingStrategy) => {
    dispatch({ type: "SET_PRICING_STRATEGY", strategy });
  });

  const canShowComparison =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const isOverridden = state.overriddenCarriers.includes(carrier);

  return (
    <Flex dir="column" gap="md" w="full">
      {isOverridden && (
        <Flex
          borderRadius="lg"
          dir="column"
          gap="xs"
          p="md"
          style={{ border: "1px solid var(--redo-colors-warning-600)" }}
        >
          <Text fontSize="sm" fontWeight="semibold" textColor="warning">
            ⚠ Eligibility Override Active
          </Text>
          <Text fontSize="xs" textColor="warning">
            This carrier's eligibility checks were overridden. The merchant may
            not actually be eligible for this carrier.
          </Text>
        </Flex>
      )}
      <Flex gap="xl">
        <Flex className={styles.sidebar} dir="column" gap="md">
          <Flex
            borderColor="primary"
            borderRadius="xl"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
          >
            <RedoTextInput
              description={
                state.totalShipments > 0
                  ? `${state.totalShipments.toLocaleString()} shipments` +
                    (state.oldestShipmentDate && state.newestShipmentDate
                      ? ` from ${new Date(state.oldestShipmentDate).toLocaleDateString()} to ${new Date(state.newestShipmentDate).toLocaleDateString()}`
                      : "")
                  : undefined
              }
              inputTypeHint="number"
              label="Daily Volume"
              setValue={(v) => {
                const volume = Number(v) || 0;
                onDailyVolumeChange(volume);
              }}
              value={String(dailyVolume)}
            />
          </Flex>

          {!isRevShareCarrier ? (
            <Flex
              borderColor="primary"
              borderRadius="xl"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
            >
              <Text fontSize="md" fontWeight="semibold">
                Pricing Strategy
              </Text>
              <Flex gap="sm">
                <StrategyButton
                  active={state.pricingStrategy === PricingStrategy.Discount}
                  description="vs merchant rate"
                  label="Discount"
                  onClick={() => handleStrategyChange(PricingStrategy.Discount)}
                />
                <StrategyButton
                  active={state.pricingStrategy === PricingStrategy.Spread}
                  description="vs carrier rate"
                  label="Spread"
                  onClick={() => handleStrategyChange(PricingStrategy.Spread)}
                />
              </Flex>
              <Flex gap="sm">
                <StrategyButton
                  active={state.unit === SpreadUnit.Percent}
                  description="percentage"
                  label="Percent"
                  onClick={() =>
                    dispatch({ type: "SET_UNIT", unit: SpreadUnit.Percent })
                  }
                />
                <StrategyButton
                  active={state.unit === SpreadUnit.Dollars}
                  description="dollar amount"
                  label="Flat"
                  onClick={() =>
                    dispatch({ type: "SET_UNIT", unit: SpreadUnit.Dollars })
                  }
                />
              </Flex>
              <RedoIncrementDecrement
                decimalPlaces={state.unit === SpreadUnit.Dollars ? 2 : 0}
                label="Amount"
                max={state.unit === SpreadUnit.Percent ? 50 : undefined}
                min={0}
                prefix={state.unit === SpreadUnit.Dollars ? "$" : undefined}
                setValue={(v) => dispatch({ type: "SET_AMOUNT", amount: v })}
                size="small"
                step={state.unit === SpreadUnit.Dollars ? 0.05 : 1}
                suffix={state.unit === SpreadUnit.Percent ? "%" : undefined}
                value={state.amount}
              />

              {state.pricingStrategy === PricingStrategy.Discount && (
                <Flex
                  bgColor="tertiary"
                  borderRadius="lg"
                  dir="column"
                  gap="sm"
                  p="md"
                >
                  <Text fontSize="sm" fontWeight="semibold">
                    No merchant data
                  </Text>
                  <Flex gap="xs">
                    <MissingRateModeButton
                      active={
                        state.missingMerchantRateMode ===
                        MissingMerchantRateMode.DefaultSpread
                      }
                      label="Default Spread"
                      onClick={() =>
                        dispatch({
                          type: "SET_MISSING_MERCHANT_RATE_MODE",
                          mode: MissingMerchantRateMode.DefaultSpread,
                        })
                      }
                    />
                    <MissingRateModeButton
                      active={state.missingMerchantRateMode === "interpolate"}
                      label="Interpolate"
                      onClick={() =>
                        dispatch({
                          type: "SET_MISSING_MERCHANT_RATE_MODE",
                          mode: MissingMerchantRateMode.Interpolate,
                        })
                      }
                    />
                  </Flex>

                  {state.missingMerchantRateMode === "defaultSpread" && (
                    <Flex align="flex-end" gap="sm">
                      <Flex grow={1}>
                        <RedoIncrementDecrement
                          decimalPlaces={
                            state.defaultSpreadUnit === SpreadUnit.Dollars
                              ? 2
                              : 0
                          }
                          label="Default spread"
                          min={0}
                          prefix={
                            state.defaultSpreadUnit === SpreadUnit.Dollars
                              ? "$"
                              : undefined
                          }
                          setValue={(v) =>
                            dispatch({
                              type: "SET_DEFAULT_SPREAD_NO_MERCHANT_RATE",
                              amount: v,
                            })
                          }
                          size="small"
                          step={
                            state.defaultSpreadUnit === SpreadUnit.Dollars
                              ? 0.05
                              : 1
                          }
                          suffix={
                            state.defaultSpreadUnit === SpreadUnit.Percent
                              ? "%"
                              : undefined
                          }
                          value={state.defaultSpreadNoMerchantRate}
                        />
                      </Flex>
                      <Flex gap="xs">
                        <UnitToggleButton
                          active={
                            state.defaultSpreadUnit === SpreadUnit.Dollars
                          }
                          label="$"
                          onClick={() =>
                            dispatch({
                              type: "SET_DEFAULT_SPREAD_UNIT",
                              unit: SpreadUnit.Dollars,
                            })
                          }
                        />
                        <UnitToggleButton
                          active={
                            state.defaultSpreadUnit === SpreadUnit.Percent
                          }
                          label="%"
                          onClick={() =>
                            dispatch({
                              type: "SET_DEFAULT_SPREAD_UNIT",
                              unit: SpreadUnit.Percent,
                            })
                          }
                        />
                      </Flex>
                    </Flex>
                  )}

                  {state.missingMerchantRateMode ===
                    MissingMerchantRateMode.Interpolate && (
                    <Text fontSize="xs" textColor="secondary">
                      Spreads interpolated from nearest populated cells
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          ) : (
            <Flex
              borderColor="primary"
              borderRadius="xl"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
            >
              <Flex align="center" gap="sm">
                <Text fontSize="md" fontWeight="semibold">
                  {rateTableCarrierLabel(carrier)} Rev Share
                </Text>
                <Flex bgColor="tertiary" borderRadius="full" px="sm" py="xs">
                  <Text fontSize="xs" textColor="secondary">
                    Passthrough
                  </Text>
                </Flex>
              </Flex>

              {carrier === RateTableCarrier.USPS ? (
                <Flex dir="column" gap="sm">
                  <RevShareDisplay
                    label="Under 1 lb"
                    value={state.revShareSettings.usps.underOneLbPct}
                  />
                  <RevShareDisplay
                    label="Over 1 lb"
                    value={state.revShareSettings.usps.overOneLbPct}
                  />
                </Flex>
              ) : (
                <Flex dir="column" gap="sm">
                  <StrategyButton
                    active={
                      state.revShareSettings.ups.activeRateType ===
                      UpsRateType.GroundSaver
                    }
                    description="UPS SurePost style"
                    label={`Ground Saver (${state.revShareSettings.ups.groundSaverPct}%)`}
                    onClick={() =>
                      dispatch({
                        type: "SET_REV_SHARE_SETTINGS",
                        settings: {
                          ...state.revShareSettings,
                          ups: {
                            ...state.revShareSettings.ups,
                            activeRateType: UpsRateType.GroundSaver,
                          },
                        },
                      })
                    }
                  />
                  <StrategyButton
                    active={
                      state.revShareSettings.ups.activeRateType ===
                      UpsRateType.OtherServices
                    }
                    description="Ground, 2-Day, etc."
                    label={`Other Services (${state.revShareSettings.ups.otherServicesPct}%)`}
                    onClick={() =>
                      dispatch({
                        type: "SET_REV_SHARE_SETTINGS",
                        settings: {
                          ...state.revShareSettings,
                          ups: {
                            ...state.revShareSettings.ups,
                            activeRateType: UpsRateType.OtherServices,
                          },
                        },
                      })
                    }
                  />
                </Flex>
              )}
            </Flex>
          )}

          {isDhlSelected && state.carrierEligibility?.dhl && (
            <Flex
              borderColor="primary"
              borderRadius="xl"
              borderWidth="1px"
              dir="column"
              gap="md"
              p="lg"
            >
              <Flex align="center" gap="sm" justify="space-between">
                <Text fontSize="md" fontWeight="semibold">
                  DHL Pickup
                </Text>
                <Text fontSize="xs" textColor="secondary">
                  {state.carrierEligibility.dhl.nearestDC}
                </Text>
              </Flex>

              <Flex align="center" gap="sm">
                <RedoCheckbox
                  setValue={(v) =>
                    dispatch({
                      type: "SET_DHL_SETTINGS",
                      settings: { ...state.dhlSettings, needsGL: v === true },
                    })
                  }
                  value={state.dhlSettings.needsGL}
                />
                <Text fontSize="sm">Needs straight truck (GL)</Text>
              </Flex>

              <RedoIncrementDecrement
                label="Pickups per week"
                max={7}
                min={1}
                setValue={(v) =>
                  dispatch({
                    type: "SET_DHL_SETTINGS",
                    settings: { ...state.dhlSettings, weeklyPickups: v },
                  })
                }
                size="small"
                step={1}
                value={state.dhlSettings.weeklyPickups}
              />

              <Flex
                bgColor="tertiary"
                borderRadius="lg"
                dir="column"
                gap="xs"
                p="md"
              >
                <Text fontSize="xs" textColor="secondary">
                  Pickup cost
                </Text>
                <Text fontSize="lg" fontWeight="bold">
                  $
                  {getDhlPickupCostPerPickup(
                    state.carrierEligibility.dhl.nearestDC,
                    state.carrierEligibility.dhl.distanceMiles,
                    state.dhlSettings.needsGL,
                  ).toFixed(0)}
                  <Text
                    as="span"
                    fontSize="sm"
                    fontWeight="regular"
                    textColor="secondary"
                  >
                    {" "}
                    /pickup
                  </Text>
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>

        <Flex
          className={styles.comparisonContainer}
          dir="column"
          gap="xl"
          grow={1}
        >
          {canShowComparison ? (
            <ComparisonRateTable
              amount={state.amount}
              carrierEligibility={state.carrierEligibility!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              dailyVolume={state.dailyVolume}
              dataSource={state.dataSource}
              defaultSpreadNoMerchantRate={state.defaultSpreadNoMerchantRate}
              defaultSpreadUnit={state.defaultSpreadUnit}
              dhlSettings={state.dhlSettings}
              exportRef={exportRef}
              missingMerchantRateMode={state.missingMerchantRateMode}
              onViewModeChange={onViewModeChange}
              pricingStrategy={state.pricingStrategy}
              rateGrid={state.rateGrid!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              rateTables={state.baselineRateTables!} // eslint-disable-line @typescript-eslint/no-non-null-assertion
              revShareSettings={state.revShareSettings}
              selectedCarrier={carrier}
              unit={state.unit}
              viewMode={viewMode}
            />
          ) : (
            <Flex
              align="center"
              dir="column"
              gap="md"
              justify="center"
              py="3xl"
            >
              <Text fontSize="md" fontWeight="semibold" textColor="secondary">
                Loading rate data...
              </Text>
              <Text fontSize="sm" textColor="secondary">
                Rebuilding rate grid from saved session
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const MixedDetailView = memo(function MixedDetailView({
  dailyVolume,
  onDailyVolumeChange,
  colorMode,
  onColorModeChange,
}: {
  dailyVolume: number;
  onDailyVolumeChange: (volume: number) => void;
  colorMode: ColorMode;
  onColorModeChange: (mode: ColorMode) => void;
}) {
  const { state, dispatch } = useRateCalculator();

  const handleStrategyChange = useHandler((strategy: PricingStrategy) => {
    dispatch({ type: "SET_PRICING_STRATEGY", strategy });
  });

  const canShowMixed =
    state.rateGrid && state.baselineRateTables && state.carrierEligibility;

  const mixedResults = useMemo(() => {
    if (!canShowMixed) {
      return null;
    }
    return calculateMixedModeResults(
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.baselineRateTables!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.rateGrid!,
      state.selectedCarriers,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      state.carrierEligibility!,
      state.pricingStrategy,
      state.unit,
      state.amount,
      state.revShareSettings,
      state.dailyVolume,
    );
  }, [
    canShowMixed,
    state.baselineRateTables,
    state.rateGrid,
    state.selectedCarriers,
    state.carrierEligibility,
    state.pricingStrategy,
    state.unit,
    state.amount,
    state.revShareSettings,
    state.dailyVolume,
  ]);

  return (
    <Flex dir="column" gap="md" w="full">
      <Flex gap="xl">
        <Flex className={styles.sidebar} dir="column" gap="md">
          <Flex
            borderColor="primary"
            borderRadius="xl"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
          >
            <RedoTextInput
              description={
                state.totalShipments > 0
                  ? `${state.totalShipments.toLocaleString()} shipments` +
                    (state.oldestShipmentDate && state.newestShipmentDate
                      ? ` from ${new Date(state.oldestShipmentDate).toLocaleDateString()} to ${new Date(state.newestShipmentDate).toLocaleDateString()}`
                      : "")
                  : undefined
              }
              inputTypeHint="number"
              label="Daily Volume"
              setValue={(v) => {
                const volume = Number(v) || 0;
                onDailyVolumeChange(volume);
              }}
              value={String(dailyVolume)}
            />
          </Flex>

          <Flex
            borderColor="primary"
            borderRadius="xl"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
          >
            <Text fontSize="md" fontWeight="semibold">
              Carrier Pricing
            </Text>
            <Text fontSize="xs" textColor="secondary">
              Applied to Amazon, UniUni
            </Text>
            <Flex gap="sm">
              <StrategyButton
                active={state.pricingStrategy === PricingStrategy.Discount}
                description="vs merchant rate"
                label="Discount"
                onClick={() => handleStrategyChange(PricingStrategy.Discount)}
              />
              <StrategyButton
                active={state.pricingStrategy === PricingStrategy.Spread}
                description="vs carrier rate"
                label="Spread"
                onClick={() => handleStrategyChange(PricingStrategy.Spread)}
              />
            </Flex>
            <Flex gap="sm">
              <StrategyButton
                active={state.unit === SpreadUnit.Percent}
                description="percentage"
                label="Percent"
                onClick={() =>
                  dispatch({ type: "SET_UNIT", unit: SpreadUnit.Percent })
                }
              />
              <StrategyButton
                active={state.unit === SpreadUnit.Dollars}
                description="dollar amount"
                label="Flat"
                onClick={() =>
                  dispatch({ type: "SET_UNIT", unit: SpreadUnit.Dollars })
                }
              />
            </Flex>
            <RedoIncrementDecrement
              decimalPlaces={state.unit === SpreadUnit.Dollars ? 2 : 0}
              label="Amount"
              max={state.unit === SpreadUnit.Percent ? 50 : undefined}
              min={0}
              prefix={state.unit === SpreadUnit.Dollars ? "$" : undefined}
              setValue={(v) => dispatch({ type: "SET_AMOUNT", amount: v })}
              size="small"
              step={state.unit === SpreadUnit.Dollars ? 0.05 : 1}
              suffix={state.unit === SpreadUnit.Percent ? "%" : undefined}
              value={state.amount}
            />
          </Flex>

          <Flex
            borderColor="primary"
            borderRadius="xl"
            borderWidth="1px"
            dir="column"
            gap="md"
            p="lg"
          >
            <Flex align="center" gap="sm">
              <Text fontSize="md" fontWeight="semibold">
                Rev Share Carriers
              </Text>
              <Flex bgColor="tertiary" borderRadius="full" px="sm" py="xs">
                <Text fontSize="xs" textColor="secondary">
                  Passthrough
                </Text>
              </Flex>
            </Flex>
            <Text fontSize="xs" textColor="secondary">
              UPS & USPS use passthrough pricing with rev share
            </Text>
            <RevShareDisplay
              label="USPS &lt;1lb"
              value={state.revShareSettings.usps.underOneLbPct}
            />
            <RevShareDisplay
              label="USPS &gt;1lb"
              value={state.revShareSettings.usps.overOneLbPct}
            />
            <RevShareDisplay
              label={`UPS (${state.revShareSettings.ups.activeRateType === UpsRateType.GroundSaver ? "GS" : "Other"})`}
              value={
                state.revShareSettings.ups.activeRateType ===
                UpsRateType.GroundSaver
                  ? state.revShareSettings.ups.groundSaverPct
                  : state.revShareSettings.ups.otherServicesPct
              }
            />
          </Flex>
        </Flex>

        <Flex
          className={styles.comparisonContainer}
          dir="column"
          gap="xl"
          grow={1}
        >
          {canShowMixed && mixedResults && state.rateGrid ? (
            <MixedModeRateTable
              colorMode={colorMode}
              dailyVolume={state.dailyVolume}
              mixedResults={mixedResults}
              onColorModeChange={onColorModeChange}
              rateGrid={state.rateGrid}
            />
          ) : (
            <Flex
              align="center"
              dir="column"
              gap="md"
              justify="center"
              py="3xl"
            >
              <Text fontSize="md" fontWeight="semibold" textColor="secondary">
                Loading rate data...
              </Text>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});

const RevShareDisplay = memo(function RevShareDisplay({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <Flex align="center" gap="md" justify="space-between">
      <Text fontSize="sm" textColor="secondary">
        {label}
      </Text>
      <Text fontSize="sm" fontWeight="semibold">
        {value}%
      </Text>
    </Flex>
  );
});

const StrategyButton = memo(function StrategyButton({
  label,
  description,
  active,
  onClick,
}: {
  label: string;
  description: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      borderRadius="lg"
      className={
        active ? styles.strategyButtonActive : styles.strategyButtonInactive
      }
      dir="column"
      gap="xs"
      onClick={onClick}
      px="md"
      py="sm"
    >
      <Text
        as="span"
        className={
          active
            ? styles.strategyButtonLabelActive
            : styles.strategyButtonLabelInactive
        }
      >
        {label}
      </Text>
      <Text
        as="span"
        className={
          active
            ? styles.strategyButtonDescriptionActive
            : styles.strategyButtonDescriptionInactive
        }
      >
        {description}
      </Text>
    </Flex>
  );
});

const MissingRateModeButton = memo(function MissingRateModeButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      borderRadius="md"
      className={
        active
          ? styles.missingRateButtonActive
          : styles.missingRateButtonInactive
      }
      onClick={onClick}
      px="sm"
      py="xs"
    >
      <Text
        as="span"
        className={
          active
            ? styles.missingRateButtonLabelActive
            : styles.missingRateButtonLabelInactive
        }
      >
        {label}
      </Text>
    </Flex>
  );
});

const UnitToggleButton = memo(function UnitToggleButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <Flex
      align="center"
      borderRadius="md"
      className={
        active ? styles.unitToggleButtonActive : styles.unitToggleButtonInactive
      }
      justify="center"
      onClick={onClick}
    >
      <Text
        as="span"
        className={
          active
            ? styles.unitToggleButtonLabelActive
            : styles.unitToggleButtonLabelInactive
        }
      >
        {label}
      </Text>
    </Flex>
  );
});
